import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Shield } from "@phosphor-icons/react";
import { toast } from "sonner";

export function FraudTab() {
  const [events, setEvents] = useState([]);
  const [blocklist, setBlocklist] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [manualType, setManualType] = useState("merchant");
  const [manualValue, setManualValue] = useState("");
  const [manualNote, setManualNote] = useState("");

  const reload = useCallback(async () => {
    try {
      const [e, b, s] = await Promise.all([
        api.get("/admin/fraud/events"),
        api.get("/admin/fraud/blocklist"),
        api.get("/admin/fraud/suggestions"),
      ]);
      setEvents(e.data.events || []);
      setBlocklist(b.data.entries || []);
      setSuggestions(s.data.suggestions || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const block = async (type, value, note = null) => {
    if (!value) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/fraud/block", { type, value, note });
      if (r.data.already_blocked) toast.info(`Already blocked: ${value}`);
      else toast.success(`Blocked ${type}: ${value}`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not block");
    } finally {
      setBusy(false);
    }
  };

  const unblock = async (entry) => {
    try {
      await api.delete(`/admin/fraud/blocklist/${entry.id}`);
      toast.success(`Unblocked ${entry.type}: ${entry.value}`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not unblock");
    }
  };

  const submitManual = async (e) => {
    e.preventDefault();
    await block(manualType, manualValue.trim(), manualNote.trim() || null);
    setManualValue("");
    setManualNote("");
  };

  return (
    <div className="space-y-10">
      <div className="bg-[#0F3320] text-[#FBF9F6] rounded-2xl p-6" data-testid="fraud-header">
        <p className="text-[10px] tracking-[0.3em] uppercase opacity-60">Fraud command center</p>
        <div className="flex items-end justify-between mt-1 flex-wrap gap-4">
          <p className="serif text-3xl">{events.length} <span className="opacity-70 text-lg">paused card{events.length === 1 ? "" : "s"}</span></p>
          <p className="serif text-3xl">{blocklist.length} <span className="opacity-70 text-lg">block{blocklist.length === 1 ? "" : "s"} active</span></p>
        </div>
        <p className="text-xs opacity-70 mt-3">
          Velocity auto-freeze fires at 6+ charges / 3 min. Click a merchant or IP below to block it — future charges from that source will be rejected with HTTP 403.
        </p>
      </div>

      {suggestions.length > 0 && (
        <div className="bg-[#D96C4A]/10 border border-[#D96C4A]/40 rounded-2xl p-5" data-testid="fraud-suggestions">
          <div className="flex items-center gap-2">
            <Shield size={18} weight="duotone" className="text-[#B04E2F]" />
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#B04E2F]">Suggested blocks · serial offenders</p>
          </div>
          <p className="text-xs text-[#4A5C50] mt-2">
            These merchants/IPs appear on <b>2+ paused cards</b> but aren't on the blocklist yet. Block globally with one click.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            {suggestions.map((s) => (
              <button
                key={`${s.type}-${s.value}`}
                onClick={() => block(s.type, s.value, `Serial offender · ${s.card_count} paused cards`)}
                disabled={busy}
                className="text-xs mono px-3 py-2 rounded-full bg-white border border-[#D96C4A]/40 hover:bg-[#D96C4A] hover:text-white text-[#112318] transition-colors"
                data-testid={`suggest-${s.type}-${s.value}`}
                title={`Block ${s.type} "${s.value}" globally`}
              >
                <span className="text-[9px] tracking-widest uppercase opacity-60 mr-1">{s.type}</span>
                {s.value}
                <span className="ml-1.5 text-[10px] text-[#B04E2F] group-hover:text-white">· {s.card_count} cards</span>
                <span className="ml-2 text-[#B04E2F]">× block</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div>
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50] mb-3">Paused cards & auto-freeze events</p>
        {events.length === 0 ? (
          <div className="bg-white border border-[#E5E0D8] rounded-2xl p-6 text-sm text-[#4A5C50]">No paused cards. Velocity defense is armed and waiting.</div>
        ) : (
          <div className="space-y-4">
            {events.map((ev) => (
              <div key={ev.card_id} className="bg-white border border-[#E5E0D8] rounded-2xl p-5" data-testid={`fraud-event-${ev.card_id}`}>
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <p className="mono text-sm text-[#4A5C50]">•••• {ev.last4}</p>
                    <p className="serif text-xl mt-0.5">{ev.receiver_name || "—"}</p>
                    <p className="text-[11px] text-[#4A5C50] mt-0.5">
                      owner · {ev.owner_email || "—"} · bal ${ev.balance_usd.toFixed(2)}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="inline-block px-2.5 py-1 rounded-full text-[10px] tracking-widest uppercase bg-[#D96C4A]/15 text-[#B04E2F]">
                      {ev.paused_reason.replace(/_/g, " ")}
                    </span>
                    <p className="text-[11px] text-[#4A5C50] mt-1 mono">
                      {ev.paused_at ? new Date(ev.paused_at).toLocaleString() : "—"}
                    </p>
                  </div>
                </div>

                {ev.recent_merchants.length > 0 && (
                  <div className="mt-4 pt-3 border-t border-[#E5E0D8]">
                    <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50] mb-2">Merchants on recent charges</p>
                    <div className="flex flex-wrap gap-2">
                      {ev.recent_merchants.map((m) => (
                        <button
                          key={m}
                          onClick={() => block("merchant", m, `Auto-freeze on card ${ev.last4}`)}
                          disabled={busy}
                          className="text-xs mono px-3 py-1.5 rounded-full bg-[#FBF9F6] border border-[#E5E0D8] hover:bg-[#D96C4A]/10 hover:border-[#D96C4A]/40 text-[#112318]"
                          data-testid={`block-merchant-${m}`}
                          title={`Block merchant "${m}"`}
                        >
                          {m} <span className="text-[#B04E2F]">× block</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {ev.recent_ips.length > 0 && (
                  <div className="mt-3">
                    <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50] mb-2">IPs</p>
                    <div className="flex flex-wrap gap-2">
                      {ev.recent_ips.map((ip) => (
                        <button
                          key={ip}
                          onClick={() => block("ip", ip, `Auto-freeze on card ${ev.last4}`)}
                          disabled={busy}
                          className="text-xs mono px-3 py-1.5 rounded-full bg-[#FBF9F6] border border-[#E5E0D8] hover:bg-[#D96C4A]/10 hover:border-[#D96C4A]/40 text-[#112318]"
                          data-testid={`block-ip-${ip}`}
                          title={`Block IP ${ip}`}
                        >
                          {ip} <span className="text-[#B04E2F]">× block</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50] mb-3">Active blocklist</p>
        <div className="bg-white border border-[#E5E0D8] rounded-2xl p-5">
          <form onSubmit={submitManual} className="flex flex-wrap items-end gap-3 mb-5" data-testid="fraud-manual-block">
            <div>
              <label className="text-[10px] tracking-widest uppercase text-[#4A5C50]">Type</label>
              <select value={manualType} onChange={(e) => setManualType(e.target.value)} className="block mt-1 h-10 px-3 rounded-lg border border-[#E5E0D8] bg-[#FBF9F6] mono text-sm">
                <option value="merchant">Merchant</option>
                <option value="ip">IP</option>
              </select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="text-[10px] tracking-widest uppercase text-[#4A5C50]">Value</label>
              <input
                type="text"
                value={manualValue}
                onChange={(e) => setManualValue(e.target.value)}
                placeholder={manualType === "merchant" ? "Exact merchant name" : "203.0.113.42"}
                className="block mt-1 h-10 w-full px-3 rounded-lg border border-[#E5E0D8] bg-[#FBF9F6] mono text-sm"
                data-testid="fraud-manual-value"
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="text-[10px] tracking-widest uppercase text-[#4A5C50]">Note (optional)</label>
              <input
                type="text"
                value={manualNote}
                onChange={(e) => setManualNote(e.target.value)}
                placeholder="Why?"
                className="block mt-1 h-10 w-full px-3 rounded-lg border border-[#E5E0D8] bg-[#FBF9F6] mono text-sm"
              />
            </div>
            <button type="submit" disabled={busy || !manualValue.trim()} className="h-10 px-5 rounded-lg bg-[#184A30] hover:bg-[#0F3320] text-[#FBF9F6] text-sm font-medium disabled:opacity-50" data-testid="fraud-manual-submit">
              Add block
            </button>
          </form>

          {blocklist.length === 0 ? (
            <p className="text-xs text-[#4A5C50]">No blocks in place. Charges from any merchant/IP pass through.</p>
          ) : (
            <div className="divide-y divide-[#E5E0D8] border-t border-[#E5E0D8]">
              {blocklist.map((b) => (
                <div key={b.id} className="flex items-center gap-3 py-3 text-sm" data-testid={`blocklist-row-${b.id}`}>
                  <span className="inline-block text-[10px] tracking-widest uppercase px-2 py-0.5 rounded-full bg-[#D96C4A]/10 text-[#B04E2F] w-20 text-center">{b.type}</span>
                  <span className="mono text-[#112318] flex-1 truncate">{b.value}</span>
                  <span className="text-[11px] text-[#4A5C50] mono hidden sm:inline">{new Date(b.created_at).toLocaleString()}</span>
                  {b.note && <span className="text-[11px] text-[#4A5C50] italic hidden md:inline">{b.note}</span>}
                  <button
                    onClick={() => unblock(b)}
                    className="h-8 px-3 rounded-lg border border-[#E5E0D8] text-[#4A5C50] text-xs hover:bg-[#FBF9F6]"
                    data-testid={`unblock-${b.id}`}
                  >
                    Unblock
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
